import { useState } from "react";
import { toast } from "sonner";
import { Question, X, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const SEGMENTS = [
  { v: "1", c: "text-sky-600", l: "Redni broj", d: "Kreće od 1 svake godine" },
  { v: "P1", c: "text-emerald-600", l: "Poslovni prostor", d: "Isto kao u ePoreznoj" },
  { v: "1", c: "text-amber-600", l: "Naplatni uređaj", d: "Obično jednostavno „1”" },
];

const TIPS = [
  "Oznaku poslovnog prostora prijavi Poreznoj upravi (ePorezna → Fiskalizacija → Poslovni prostori) — ovdje mora pisati identično.",
  "Svaki objekt je zaseban poslovni prostor, s vlastitim brojačem koji svake godine kreće iznova od 1.",
  "„Početni broj” upiši samo ako si ove godine već izdavao račune izvan Lodgyja — inače ostavi 1.",
];

function NumberingHelpModal({ onClose }) {
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-2xl" data-testid="numbering-help-modal">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-base font-semibold">Kako upisati brojeve računa</h4>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted" data-testid="numbering-help-close"><X size={16} /></button>
        </div>
        <div className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary/40 bg-primary/5 py-5 font-mono text-3xl font-bold">
          {SEGMENTS.map((s, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <span className="text-muted-foreground/50">-</span>}
              <span className={s.c}>{s.v}</span>
            </span>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          {SEGMENTS.map((s, i) => (
            <div key={i}>
              <span className={`mx-auto mb-1.5 block h-1.5 w-8 rounded-full ${s.c.replace("text-", "bg-")}`} />
              <p className="text-[11px] font-semibold text-foreground">{s.l}</p>
              <p className="text-[10.5px] text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <ul className="mt-5 space-y-2.5">
          {TIPS.map((tip, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-primary" /> {tip}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

export function NumberingHelpButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
        data-testid="numbering-help-btn"
      >
        <Question size={13} weight="bold" /> Kako upisati brojeve?
      </button>
      {open && <NumberingHelpModal onClose={() => setOpen(false)} />}
    </>
  );
}

const FIELDS = [
  ["invoice_premise_code", "Oznaka poslovnog prostora *", "npr. P1", "text"],
  ["invoice_device_code", "Oznaka naplatnog uređaja", "1", "text"],
  ["invoice_start_number", "Početni broj (ova godina)", "1", "number"],
];

/** Per-property fiscal numbering inputs (saved on blur). */
export default function PropertyNumbering({ property, onSaved }) {
  const [v, setV] = useState({
    invoice_premise_code: property.invoice_premise_code || "",
    invoice_device_code: property.invoice_device_code || "1",
    invoice_start_number: property.invoice_start_number || 1,
  });
  const save = async (k) => {
    const val = k === "invoice_start_number" ? Math.max(1, parseInt(v[k], 10) || 1) : String(v[k] || "").trim().replace(/[^A-Za-z0-9]/g, "");
    if (val === (property[k] ?? (k === "invoice_device_code" ? "1" : k === "invoice_start_number" ? 1 : ""))) return;
    try {
      const patch = { [k]: val || null };
      if (k === "invoice_start_number") patch.invoice_start_year = new Date().getFullYear();
      await api.put(`/properties/${property.id}`, patch);
      toast.success("Numeracija spremljena");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const year = new Date().getFullYear();
  return (
    <div className="mt-3 grid grid-cols-1 gap-2 border-t border-dashed border-border pt-3 sm:grid-cols-4" data-testid={`numbering-${property.id}`}>
      {FIELDS.map(([k, label, ph, type]) => (
        <label key={k} className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
          <input type={type} min={type === "number" ? 1 : undefined} value={v[k]} placeholder={ph} onChange={(e) => setV((p) => ({ ...p, [k]: e.target.value }))} onBlur={() => save(k)}
            className={`rw-input w-full mono ${k === "invoice_premise_code" && !v[k] ? "border-amber-400" : ""}`} data-testid={`${k.replace(/_/g, "-")}-${property.id}`} />
        </label>
      ))}
      <div className="self-end pb-2 text-[11px] text-muted-foreground">
        Format: <span className="font-mono font-semibold text-foreground">{v.invoice_premise_code ? `N-${v.invoice_premise_code}-${v.invoice_device_code || 1}` : "postavite oznaku"}</span>
        <span className="block">brojač za {year}. kreće od {v.invoice_start_number || 1}</span>
      </div>
    </div>
  );
}
